import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from './lib/axiosClient';
import useAutoRefresh from './hooks/useAutoRefresh';

const VEHICLE_ICONS = { motorcycle: '🏍️', scooter: '🛵', atv: '🏎️', utv: '🚙', dirt_bike: '🏍️', trike: '🛞', car: '🚗', truck: '🚛', suv: '🚙', other: '🔧' };
const COMMON_MAKES = ['Harley-Davidson','Honda','Yamaha','Kawasaki','Suzuki','Ducati','BMW','Triumph','Indian','KTM','Aprilia','Royal Enfield','Husqvarna','Can-Am','Polaris','Other'];
const VEHICLE_TYPES = [
  { key: 'motorcycle', label: 'Motorcycle' },{ key: 'scooter', label: 'Scooter' },{ key: 'atv', label: 'ATV' },
  { key: 'utv', label: 'UTV' },{ key: 'dirt_bike', label: 'Dirt Bike' },{ key: 'trike', label: 'Trike' },
  { key: 'car', label: 'Car' },{ key: 'truck', label: 'Truck' },{ key: 'suv', label: 'SUV' },{ key: 'other', label: 'Other' },
];

export default function Vehicles() {
  const [vehicles, setVehicles] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('grid');
  const [search, setSearch] = useState('');
  const [makeFilter, setMakeFilter] = useState('All');
  const [showModal, setShowModal] = useState(false);
  const [editVehicle, setEditVehicle] = useState(null);
  const [historyPanel, setHistoryPanel] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [remindersPanel, setRemindersPanel] = useState(null);
  const [remindersLoading, setRemindersLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [sortBy, setSortBy] = useState('newest');

  const fetchData = useCallback(async () => {
    try {
      const [vr, cr] = await Promise.all([
        axios.get('/api/vehicles'),
        axios.get('/api/customers?limit=500').catch(() => ({ data: { customers: [] } })),
      ]);
      setVehicles(vr.data.vehicles || []);
      setCustomers(cr.data.customers || cr.data.contacts || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);
  useAutoRefresh(fetchData, 20000);

  const loadHistory = async (id) => {
    setHistoryLoading(true);
    try { var r = await axios.get('/api/vehicles/' + id + '/history'); if (r.data.success) setHistoryPanel(r.data); }
    catch (e) { alert('Failed to load history'); }
    setHistoryLoading(false);
  };

  const loadReminders = async (id) => {
    setRemindersLoading(true);
    try { var r = await axios.get('/api/vehicles/' + id + '/service-reminders'); if (r.data.success) setRemindersPanel(r.data); }
    catch (e) { alert('Failed to load reminders'); }
    setRemindersLoading(false);
  };

  const updateMileage = async (id, mileage) => {
    try { await axios.put('/api/vehicles/' + id + '/mileage', { mileage }); fetchData(); loadReminders(id); }
    catch (e) { alert('Failed'); }
  };

  const saveVehicle = async (form) => {
    try {
      if (form.id) await axios.put('/api/vehicles/' + form.id, form);
      else await axios.post('/api/vehicles', form);
      setShowModal(false); setEditVehicle(null); fetchData();
    } catch (e) { alert(e.response?.data?.error || 'Failed'); }
  };

  const deleteVehicle = async (id) => {
    if (!window.confirm('Delete this vehicle?')) return;
    try { await axios.delete('/api/vehicles/' + id); fetchData(); } catch (e) { alert('Failed'); }
  };

  const bulkDelete = async () => {
    if (!selectedIds.length || !window.confirm('Delete ' + selectedIds.length + ' vehicles?')) return;
    for (var id of selectedIds) { await axios.delete('/api/vehicles/' + id).catch(() => {}); }
    setSelectedIds([]); fetchData();
  };

  const makes = useMemo(() => ['All', ...Array.from(new Set(vehicles.map(v => v.make).filter(Boolean))).sort()], [vehicles]);

  const filtered = useMemo(() => {
    var q = search.toLowerCase();
    var list = vehicles.filter(v => {
      var ms = !q || (v.make || '').toLowerCase().includes(q) || (v.model || '').toLowerCase().includes(q) ||
        (v.license_plate || '').toLowerCase().includes(q) || (v.vin || '').toLowerCase().includes(q) ||
        (v.contacts?.first_name || '').toLowerCase().includes(q) || (v.contacts?.last_name || '').toLowerCase().includes(q);
      var mm = makeFilter === 'All' || v.make === makeFilter;
      return ms && mm;
    });
    list.sort((a, b) => {
      if (sortBy === 'newest') return new Date(b.created_at || 0) - new Date(a.created_at || 0);
      if (sortBy === 'oldest') return new Date(a.created_at || 0) - new Date(b.created_at || 0);
      if (sortBy === 'make') return (a.make || '').localeCompare(b.make || '');
      if (sortBy === 'year') return (b.year || 0) - (a.year || 0);
      if (sortBy === 'mileage') return (b.mileage || 0) - (a.mileage || 0);
      if (sortBy === 'owner') return ((a.contacts?.first_name || '') + (a.contacts?.last_name || '')).localeCompare((b.contacts?.first_name || '') + (b.contacts?.last_name || ''));
      return 0;
    });
    return list;
  }, [vehicles, search, makeFilter, sortBy]);

  // Stats
  var totalMileage = vehicles.reduce((s, v) => s + (v.mileage || 0), 0);
  var avgMileage = vehicles.length > 0 ? Math.round(totalMileage / vehicles.length) : 0;
  var typeCounts = {};
  vehicles.forEach(v => { var t = v.vehicle_type || 'motorcycle'; typeCounts[t] = (typeCounts[t] || 0) + 1; });
  var topType = Object.entries(typeCounts).sort((a, b) => b[1] - a[1])[0];
  var owners = new Set(vehicles.map(v => v.contact_id).filter(Boolean)).size;

  var S = {
    input: { width: '100%', padding: '12px 16px', background: '#1a1c2e', border: '1px solid #1e1e35', borderRadius: '10px', color: '#e2e2e8', fontSize: '14px', outline: 'none', boxSizing: 'border-box' },
    label: { fontSize: '13px', fontWeight: '600', color: '#888', marginBottom: '6px', display: 'block' },
    card: { background: '#12121f', border: '1px solid #1e1e35', borderRadius: '16px', padding: '20px' },
    pill: (active) => ({ padding: '7px 14px', borderRadius: '8px', border: 'none', fontSize: '12px', fontWeight: '600', cursor: 'pointer', background: active ? '#ff6b2b' : 'transparent', color: active ? '#fff' : '#888' }),
    viewBtn: (active) => ({ padding: '8px 12px', borderRadius: '8px', border: active ? '1px solid #ff6b2b' : '1px solid #1e1e35', background: active ? '#ff6b2b15' : 'transparent', color: active ? '#ff6b2b' : '#888', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }),
    badge: (color) => ({ display: 'inline-block', padding: '3px 8px', borderRadius: '6px', fontSize: '11px', fontWeight: '700', background: color + '20', color }),
  };

  if (loading) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', color: '#888' }}><p>Loading vehicles...</p></div>;

  return (
    <div style={{ maxWidth: '100%' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h1 style={{ fontSize: '26px', fontWeight: '800', color: '#fff', margin: '0 0 6px' }}>Vehicles</h1>
          <p style={{ fontSize: '14px', color: '#888', margin: 0 }}>{vehicles.length} registered · {owners} owners</p>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          {selectedIds.length > 0 && (
            <button onClick={bulkDelete} style={{ padding: '10px 16px', background: '#d63031', border: 'none', borderRadius: '10px', color: '#fff', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>
              🗑️ Delete {selectedIds.length}
            </button>
          )}
          <button onClick={() => { setEditVehicle(null); setShowModal(true); }} style={{ padding: '10px 20px', background: 'linear-gradient(135deg, #ff6b2b, #ffab40)', border: 'none', borderRadius: '10px', color: '#fff', fontSize: '13px', fontWeight: '700', cursor: 'pointer' }}>
            + Add Vehicle
          </button>
        </div>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '10px', marginBottom: '20px' }}>
        {[
          { label: 'Total', value: vehicles.length, color: '#ff6b2b' },
          { label: 'Owners', value: owners, color: '#0984e3' },
          { label: 'Makes', value: makes.length - 1, color: '#6c5ce7' },
          { label: 'Avg Mileage', value: avgMileage.toLocaleString() + ' mi', color: '#00b894', small: true },
          { label: 'Top Type', value: topType ? topType[0].replace('_', ' ') : '—', color: '#888', small: true },
        ].map((s, i) => (
          <div key={i} style={{ ...S.card, padding: '16px' }}>
            <p style={{ fontSize: '10px', color: '#666', fontWeight: '700', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{s.label}</p>
            <p style={{ fontSize: s.small ? '15px' : '22px', fontWeight: '800', color: s.color, margin: 0, textTransform: 'capitalize' }}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Controls */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: '4px', background: '#1a1c2e', borderRadius: '10px', padding: '4px' }}>
          <button onClick={() => setView('grid')} style={S.viewBtn(view === 'grid')}>▦ Grid</button>
          <button onClick={() => setView('table')} style={S.viewBtn(view === 'table')}>☰ Table</button>
        </div>
        <input placeholder="Search make, model, plate, VIN, owner..." value={search} onChange={e => setSearch(e.target.value)}
          style={{ ...S.input, maxWidth: '280px', flex: '1 1 200px' }} />
        {makes.length > 2 && (
          <div style={{ display: 'flex', gap: '3px', background: '#1a1c2e', borderRadius: '10px', padding: '4px', flexWrap: 'wrap' }}>
            {makes.slice(0, 8).map(m => <button key={m} onClick={() => setMakeFilter(m)} style={S.pill(makeFilter === m)}>{m}</button>)}
            {makes.length > 8 && <span style={{ color: '#555', padding: '7px 8px', fontSize: '12px' }}>+{makes.length - 8}</span>}
          </div>
        )}
        <select value={sortBy} onChange={e => setSortBy(e.target.value)} style={{ ...S.input, maxWidth: '170px', cursor: 'pointer' }}>
          <option value="newest">Sort: Newest</option>
          <option value="oldest">Sort: Oldest</option>
          <option value="make">Sort: Make</option>
          <option value="year">Sort: Year ↓</option>
          <option value="mileage">Sort: Mileage ↓</option>
          <option value="owner">Sort: Owner</option>
        </select>
      </div>

      {/* ══════ GRID VIEW ══════ */}
      {view === 'grid' && (
        filtered.length === 0 ? <Empty search={search} count={vehicles.length} /> : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '14px' }}>
            {filtered.map(v => {
              var icon = VEHICLE_ICONS[v.vehicle_type] || '🏍️';
              return (
                <div key={v.id} style={{ ...S.card, display: 'flex', flexDirection: 'column', transition: 'border-color 0.2s' }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = '#ff6b2b40'}
                  onMouseLeave={e => e.currentTarget.style.borderColor = '#1e1e35'}>
                  {/* Top */}
                  <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
                    <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: '#1a1c2e', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '26px', flexShrink: 0 }}>{icon}</div>
                    <div style={{ flex: 1 }}>
                      <h3 style={{ color: '#fff', fontSize: '15px', fontWeight: '700', margin: '0 0 2px' }}>{v.year} {v.make} {v.model}</h3>
                      {v.contacts && <p style={{ color: '#888', fontSize: '12px', margin: 0 }}>{v.contacts.first_name} {v.contacts.last_name}</p>}
                    </div>
                    {v.color && <div style={{ width: '14px', height: '14px', borderRadius: '50%', border: '2px solid #2a2a45', background: v.color.toLowerCase(), flexShrink: 0, marginTop: '4px' }} title={v.color} />}
                  </div>

                  {/* Details */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 14px', marginBottom: '12px', fontSize: '12px' }}>
                    {v.license_plate && <D label="Plate" value={v.license_plate} />}
                    {v.mileage && <D label="Mileage" value={Number(v.mileage).toLocaleString() + ' mi'} />}
                    {v.engine_size && <D label="Engine" value={v.engine_size} />}
                    {v.vin && <D label="VIN" value={v.vin.slice(-6)} title={v.vin} />}
                    {v.vehicle_type && v.vehicle_type !== 'motorcycle' && <D label="Type" value={v.vehicle_type.replace('_', ' ')} />}
                  </div>

                  {v.notes && <p style={{ color: '#555', fontSize: '11px', margin: '0 0 10px', fontStyle: 'italic', lineHeight: '1.4' }}>{v.notes}</p>}

                  {/* Actions */}
                  <div style={{ display: 'flex', gap: '6px', marginTop: 'auto', paddingTop: '12px', borderTop: '1px solid #1e1e35' }}>
                    <button onClick={() => loadHistory(v.id)} style={ab('#0984e3')}>📋 History</button>
                    <button onClick={() => loadReminders(v.id)} style={ab('#f59e0b')}>🔔 Service</button>
                    <button onClick={() => { setEditVehicle(v); setShowModal(true); }} style={ab('#888')}>Edit</button>
                    <button onClick={() => deleteVehicle(v.id)} style={{ padding: '6px 10px', background: '#d6303110', border: 'none', borderRadius: '8px', color: '#d63031', fontSize: '11px', fontWeight: '600', cursor: 'pointer' }}>✕</button>
                  </div>
                </div>
              );
            })}
          </div>
        )
      )}

      {/* ══════ TABLE VIEW ══════ */}
      {view === 'table' && (
        filtered.length === 0 ? <Empty search={search} count={vehicles.length} /> : (
          <div style={{ ...S.card, padding: 0, overflow: 'hidden' }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '900px' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #1e1e35' }}>
                    <th style={th(36)}><input type="checkbox" checked={selectedIds.length === filtered.length && filtered.length > 0}
                      onChange={() => setSelectedIds(selectedIds.length === filtered.length ? [] : filtered.map(v => v.id))}
                      style={{ width: '15px', height: '15px', accentColor: '#ff6b2b', cursor: 'pointer' }} /></th>
                    {['Vehicle', 'Owner', 'Plate', 'VIN', 'Mileage', 'Engine', 'Color', ''].map(h => <th key={h} style={th()}>{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(v => (
                    <tr key={v.id} style={{ borderBottom: '1px solid #1e1e3515' }}>
                      <td style={td()}><input type="checkbox" checked={selectedIds.includes(v.id)}
                        onChange={() => setSelectedIds(p => p.includes(v.id) ? p.filter(x => x !== v.id) : [...p, v.id])}
                        style={{ width: '15px', height: '15px', accentColor: '#ff6b2b', cursor: 'pointer' }} /></td>
                      <td style={td()}>
                        <span style={{ fontSize: '16px', marginRight: '6px' }}>{VEHICLE_ICONS[v.vehicle_type] || '🏍️'}</span>
                        <span style={{ color: '#fff', fontWeight: '600', fontSize: '13px' }}>{v.year} {v.make} {v.model}</span>
                      </td>
                      <td style={td()}>{v.contacts ? <span style={{ color: '#aaa', fontSize: '13px' }}>{v.contacts.first_name} {v.contacts.last_name}</span> : <span style={{ color: '#444' }}>—</span>}</td>
                      <td style={td()}><span style={{ color: '#ff6b2b', fontWeight: '700', fontSize: '12px', letterSpacing: '0.5px' }}>{v.license_plate || '—'}</span></td>
                      <td style={td()}><span style={{ color: '#888', fontSize: '12px', fontFamily: 'monospace' }}>{v.vin ? '...' + v.vin.slice(-6) : '—'}</span></td>
                      <td style={td()}>{v.mileage ? <span style={{ color: '#aaa', fontSize: '13px' }}>{Number(v.mileage).toLocaleString()}</span> : <span style={{ color: '#444' }}>—</span>}</td>
                      <td style={td()}><span style={{ color: '#888', fontSize: '12px' }}>{v.engine_size || '—'}</span></td>
                      <td style={td()}>{v.color ? <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><div style={{ width: '10px', height: '10px', borderRadius: '50%', background: v.color.toLowerCase(), border: '1px solid #2a2a45' }} /><span style={{ color: '#888', fontSize: '12px' }}>{v.color}</span></div> : '—'}</td>
                      <td style={td()}>
                        <div style={{ display: 'flex', gap: '4px' }}>
                          <button onClick={() => loadHistory(v.id)} style={tb('#0984e3')}>📋</button>
                          <button onClick={() => loadReminders(v.id)} style={tb('#f59e0b')}>🔔</button>
                          <button onClick={() => { setEditVehicle(v); setShowModal(true); }} style={tb('#888')}>✏️</button>
                          <button onClick={() => deleteVehicle(v.id)} style={tb('#d63031')}>✕</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{ padding: '10px 14px', borderTop: '1px solid #1e1e35', fontSize: '12px', color: '#666' }}>
              Showing {filtered.length} of {vehicles.length}
            </div>
          </div>
        )
      )}

      {/* ══════ ADD/EDIT MODAL ══════ */}
      {showModal && <VehicleModal vehicle={editVehicle} customers={customers} onSave={saveVehicle} onClose={() => { setShowModal(false); setEditVehicle(null); }} S={S} />}

      {/* ══════ HISTORY PANEL ══════ */}
      {(historyPanel || historyLoading) && (
        <Overlay onClose={() => setHistoryPanel(null)}>
          {historyLoading ? <div style={{ padding: '60px', textAlign: 'center', color: '#888' }}>Loading history...</div>
            : historyPanel && <HistoryPanel data={historyPanel} onClose={() => setHistoryPanel(null)} S={S} />}
        </Overlay>
      )}

      {/* ══════ REMINDERS PANEL ══════ */}
      {(remindersPanel || remindersLoading) && (
        <Overlay onClose={() => setRemindersPanel(null)}>
          {remindersLoading ? <div style={{ padding: '60px', textAlign: 'center', color: '#888' }}>Loading reminders...</div>
            : remindersPanel && <RemindersPanel data={remindersPanel} onClose={() => setRemindersPanel(null)} onUpdateMileage={updateMileage} S={S} />}
        </Overlay>
      )}
    </div>
  );
}

// ── Helpers ──
function D({ label, value, title }) {
  return <div><span style={{ color: '#555', fontWeight: '600' }}>{label}: </span><span style={{ color: '#aaa' }} title={title}>{value}</span></div>;
}
function Empty({ search, count }) {
  return (
    <div style={{ textAlign: 'center', padding: '64px 24px', background: '#12121f', borderRadius: '20px', border: '1px solid #1e1e35' }}>
      <p style={{ fontSize: '40px', marginBottom: '12px' }}>🏍️</p>
      <p style={{ color: '#fff', fontSize: '18px', fontWeight: '700', marginBottom: '4px' }}>{count === 0 ? 'No vehicles yet' : 'No matches'}</p>
      <p style={{ color: '#888', fontSize: '14px' }}>{count === 0 ? 'Add your first vehicle to get started' : 'Try adjusting your search or filters'}</p>
    </div>
  );
}
function Overlay({ children, onClose }) {
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: '#000000cc', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 1000, padding: '30px 16px', overflowY: 'auto' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#12121f', borderRadius: '20px', border: '1px solid #1e1e35', width: '100%', maxWidth: '640px', maxHeight: '90vh', overflow: 'auto' }}>
        {children}
      </div>
    </div>
  );
}
var ab = (c) => ({ padding: '6px 10px', background: c + '15', border: 'none', borderRadius: '8px', color: c, fontSize: '11px', fontWeight: '600', cursor: 'pointer' });
var tb = (c) => ({ padding: '4px 8px', background: c + '15', border: 'none', borderRadius: '6px', color: c, fontSize: '12px', cursor: 'pointer' });
var th = (w) => ({ padding: '12px 10px', textAlign: 'left', fontSize: '10px', fontWeight: '700', color: '#555', textTransform: 'uppercase', letterSpacing: '0.5px', width: w || 'auto' });
var td = () => ({ padding: '10px' });

// ══════════════════════════════════════════════
// VEHICLE MODAL
// ══════════════════════════════════════════════
function VehicleModal({ vehicle, customers, onSave, onClose, S }) {
  var [form, setForm] = useState({
    contact_id: vehicle?.contact_id || '', make: vehicle?.make || '', model: vehicle?.model || '',
    year: vehicle?.year || '', vin: vehicle?.vin || '', license_plate: vehicle?.license_plate || '',
    color: vehicle?.color || '', mileage: vehicle?.mileage || '', engine_size: vehicle?.engine_size || '',
    vehicle_type: vehicle?.vehicle_type || 'motorcycle', notes: vehicle?.notes || '', id: vehicle?.id || null,
  });
  var u = (k, v) => setForm(p => ({ ...p, [k]: v }));
  var [custSearch, setCustSearch] = useState('');
  var filteredCusts = customers.filter(c => !custSearch || (c.first_name + ' ' + (c.last_name || '') + ' ' + (c.phone || '')).toLowerCase().includes(custSearch.toLowerCase())).slice(0, 15);

  var selectedCust = customers.find(c => String(c.id) === String(form.contact_id));

  return (
    <Overlay onClose={onClose}>
      <div style={{ padding: '28px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '24px' }}>
          <h3 style={{ color: '#fff', fontSize: '18px', fontWeight: '700', margin: 0 }}>{vehicle ? 'Edit Vehicle' : 'Add Vehicle'}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#888', fontSize: '20px', cursor: 'pointer' }}>✕</button>
        </div>

        <div style={{ display: 'grid', gap: '14px' }}>
          {/* Owner */}
          <div>
            <label style={S.label}>Owner *</label>
            {selectedCust && !custSearch ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{ ...S.input, flex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderColor: '#00b894' }}>
                  <span style={{ color: '#fff' }}>{selectedCust.first_name} {selectedCust.last_name} {selectedCust.phone ? '· ' + selectedCust.phone : ''}</span>
                  <span style={{ color: '#00b894' }}>✓</span>
                </div>
                <button onClick={() => { u('contact_id', ''); setCustSearch(' '); }} style={{ padding: '12px', background: '#1e1e35', border: 'none', borderRadius: '10px', color: '#aaa', cursor: 'pointer', fontSize: '12px' }}>Change</button>
              </div>
            ) : (
              <div>
                <input placeholder="Search customer..." value={custSearch} onChange={e => setCustSearch(e.target.value)} style={S.input} autoFocus />
                <div style={{ maxHeight: '140px', overflowY: 'auto', marginTop: '4px' }}>
                  {filteredCusts.map(c => (
                    <div key={c.id} onClick={() => { u('contact_id', c.id); setCustSearch(''); }}
                      style={{ padding: '8px 12px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #1e1e3520' }}
                      onMouseEnter={e => e.currentTarget.style.background = '#252840'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <span style={{ color: '#fff', fontSize: '13px', fontWeight: '600' }}>{c.first_name} {c.last_name}</span>
                      <span style={{ color: '#666', fontSize: '12px' }}>{c.phone || ''}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Make / Model */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
            <div>
              <label style={S.label}>Make *</label>
              <select value={COMMON_MAKES.includes(form.make) ? form.make : 'Other'} onChange={e => u('make', e.target.value === 'Other' ? '' : e.target.value)} style={{ ...S.input, cursor: 'pointer' }}>
                <option value="">Select...</option>
                {COMMON_MAKES.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
              {!COMMON_MAKES.includes(form.make) && <input value={form.make} onChange={e => u('make', e.target.value)} placeholder="Custom make..." style={{ ...S.input, marginTop: '6px' }} />}
            </div>
            <div><label style={S.label}>Model *</label><input value={form.model} onChange={e => u('model', e.target.value)} placeholder="e.g. Street Glide" style={S.input} /></div>
          </div>

          {/* Year / Color / Engine */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px' }}>
            <div><label style={S.label}>Year</label><input type="number" value={form.year} onChange={e => u('year', e.target.value)} placeholder="2024" style={S.input} /></div>
            <div><label style={S.label}>Color</label><input value={form.color} onChange={e => u('color', e.target.value)} placeholder="Black" style={S.input} /></div>
            <div><label style={S.label}>Engine</label><input value={form.engine_size} onChange={e => u('engine_size', e.target.value)} placeholder="1200cc" style={S.input} /></div>
          </div>

          {/* Plate / Mileage */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
            <div><label style={S.label}>License Plate</label><input value={form.license_plate} onChange={e => u('license_plate', e.target.value.toUpperCase())} placeholder="ABC 1234" style={S.input} /></div>
            <div><label style={S.label}>Mileage</label><input type="number" value={form.mileage} onChange={e => u('mileage', e.target.value)} placeholder="12500" style={S.input} /></div>
          </div>

          {/* VIN */}
          <div><label style={S.label}>VIN</label><input value={form.vin} onChange={e => u('vin', e.target.value.toUpperCase())} placeholder="17-character VIN" maxLength={17} style={S.input} /></div>

          {/* Type */}
          <div>
            <label style={S.label}>Type</label>
            <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
              {VEHICLE_TYPES.map(t => (
                <button key={t.key} onClick={() => u('vehicle_type', t.key)} type="button"
                  style={{ padding: '6px 12px', borderRadius: '8px', border: form.vehicle_type === t.key ? '1px solid #ff6b2b' : '1px solid #1e1e35', background: form.vehicle_type === t.key ? '#ff6b2b15' : '#1a1c2e', color: form.vehicle_type === t.key ? '#ff6b2b' : '#888', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}>
                  {VEHICLE_ICONS[t.key] || '🔧'} {t.label}
                </button>
              ))}
            </div>
          </div>

          {/* Notes */}
          <div><label style={S.label}>Notes</label><textarea value={form.notes} onChange={e => u('notes', e.target.value)} placeholder="Any notes..." rows={2} style={{ ...S.input, resize: 'vertical' }} /></div>
        </div>

        <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
          <button onClick={onClose} style={{ flex: 1, padding: '14px', background: '#1e1e35', border: 'none', borderRadius: '10px', color: '#aaa', fontWeight: '600', cursor: 'pointer' }}>Cancel</button>
          <button onClick={() => onSave(form)} style={{ flex: 1, padding: '14px', background: 'linear-gradient(135deg, #ff6b2b, #ffab40)', border: 'none', borderRadius: '10px', color: '#fff', fontWeight: '700', cursor: 'pointer' }}>{vehicle ? 'Save Changes' : 'Add Vehicle'}</button>
        </div>
      </div>
    </Overlay>
  );
}

// ══════════════════════════════════════════════
// HISTORY PANEL
// ══════════════════════════════════════════════
function HistoryPanel({ data, onClose }) {
  var { vehicle, history } = data;
  var { stats, timeline } = history;
  var typeC = {
    work_order: { icon: '🔧', label: 'Work Order', color: '#f59e0b' },
    estimate: { icon: '📝', label: 'Estimate', color: '#6c5ce7' },
    invoice: { icon: '📄', label: 'Invoice', color: '#0984e3' },
  };
  var statusC = (s) => {
    var m = { completed: '#00b894', paid: '#00b894', delivered: '#00b894', picked_up: '#00b894', approved: '#0984e3', accepted: '#0984e3', in_progress: '#f59e0b', pending: '#f59e0b', sent: '#f59e0b', received: '#ffab40', diagnosed: '#6c5ce7', ready: '#00cec9', quality_check: '#00cec9', cancelled: '#636e72', declined: '#d63031', overdue: '#d63031' };
    return m[s] || '#888';
  };

  return (
    <div>
      <div style={{ padding: '20px 24px', borderBottom: '1px solid #1e1e35', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h3 style={{ color: '#fff', fontSize: '18px', fontWeight: '700', margin: '0 0 2px' }}>📋 {vehicle.year} {vehicle.make} {vehicle.model}</h3>
          <p style={{ color: '#888', fontSize: '12px', margin: 0 }}>
            {vehicle.contacts?.first_name} {vehicle.contacts?.last_name}
            {vehicle.vin && ' · VIN: ...' + vehicle.vin.slice(-6)}
            {vehicle.license_plate && ' · ' + vehicle.license_plate}
          </p>
        </div>
        <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#888', fontSize: '18px', cursor: 'pointer' }}>✕</button>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px', padding: '16px 24px' }}>
        {[
          { label: 'Total Visits', value: stats.total_visits, color: '#0984e3' },
          { label: 'Total Spent', value: '$' + stats.total_spent.toFixed(0), color: '#00b894' },
          { label: 'Open Orders', value: stats.open_work_orders, color: '#f59e0b' },
          { label: 'Last Visit', value: stats.last_visit ? new Date(stats.last_visit).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : 'N/A', color: '#6c5ce7' },
        ].map((s, i) => (
          <div key={i} style={{ background: '#1a1c2e', borderRadius: '12px', padding: '14px', textAlign: 'center', border: '1px solid #1e1e35' }}>
            <div style={{ fontSize: '20px', fontWeight: '800', color: s.color }}>{s.value}</div>
            <div style={{ fontSize: '10px', fontWeight: '700', color: '#555', marginTop: '2px', textTransform: 'uppercase' }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Timeline */}
      <div style={{ padding: '8px 24px 24px' }}>
        <h4 style={{ color: '#fff', fontSize: '14px', fontWeight: '700', marginBottom: '14px' }}>Service History</h4>
        {timeline.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#555' }}>
            <p style={{ fontSize: '32px', marginBottom: '8px' }}>📋</p>
            <p>No service history yet</p>
          </div>
        ) : (
          <div style={{ position: 'relative', paddingLeft: '28px' }}>
            <div style={{ position: 'absolute', left: '10px', top: '8px', bottom: '8px', width: '2px', background: '#1e1e35' }} />
            {timeline.map((item, i) => {
              var cfg = typeC[item.type] || typeC.work_order;
              return (
                <div key={item.type + '-' + item.id} style={{ position: 'relative', marginBottom: '12px' }}>
                  <div style={{ position: 'absolute', left: '-22px', top: '14px', width: '12px', height: '12px', borderRadius: '50%', background: cfg.color, border: '3px solid #12121f' }} />
                  <div style={{ background: '#1a1c2e', border: '1px solid #1e1e35', borderRadius: '12px', padding: '12px 16px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ background: cfg.color + '20', color: cfg.color, padding: '2px 8px', borderRadius: '6px', fontSize: '10px', fontWeight: '700' }}>{cfg.icon} {cfg.label}</span>
                        <span style={{ color: '#fff', fontSize: '13px', fontWeight: '700' }}>{item.ref}</span>
                      </div>
                      <span style={{ background: statusC(item.status) + '20', color: statusC(item.status), padding: '2px 8px', borderRadius: '6px', fontSize: '10px', fontWeight: '700' }}>
                        {(item.status || '').replace(/_/g, ' ').toUpperCase()}
                      </span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        {item.title && <p style={{ color: '#aaa', fontSize: '12px', margin: '2px 0' }}>{item.title}</p>}
                        <p style={{ color: '#555', fontSize: '11px', margin: 0 }}>{new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</p>
                      </div>
                      {item.amount && <span style={{ color: '#00b894', fontWeight: '800', fontSize: '15px' }}>${parseFloat(item.amount).toFixed(0)}</span>}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════
// REMINDERS PANEL
// ══════════════════════════════════════════════
function RemindersPanel({ data, onClose, onUpdateMileage }) {
  var { vehicle, reminders } = data;
  var [editMileage, setEditMileage] = useState(false);
  var [mileageVal, setMileageVal] = useState(vehicle.mileage || 0);
  var uc = { overdue: { label: 'OVERDUE', color: '#d63031', icon: '🔴' }, due_soon: { label: 'DUE SOON', color: '#f59e0b', icon: '🟡' }, ok: { label: 'OK', color: '#00b894', icon: '🟢' } };

  return (
    <div>
      <div style={{ padding: '20px 24px', borderBottom: '1px solid #1e1e35', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h3 style={{ color: '#fff', fontSize: '18px', fontWeight: '700', margin: '0 0 2px' }}>🔔 Service Reminders</h3>
          <p style={{ color: '#888', fontSize: '12px', margin: 0 }}>{vehicle.year} {vehicle.make} {vehicle.model}</p>
        </div>
        <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#888', fontSize: '18px', cursor: 'pointer' }}>✕</button>
      </div>

      {/* Mileage */}
      <div style={{ padding: '16px 24px', background: '#1a1c2e', borderBottom: '1px solid #1e1e35' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <p style={{ fontSize: '10px', fontWeight: '700', color: '#555', textTransform: 'uppercase', marginBottom: '2px' }}>Current Mileage</p>
            {!editMileage ? (
              <p style={{ fontSize: '28px', fontWeight: '800', color: '#fff', margin: 0 }}>{(vehicle.mileage || 0).toLocaleString()} mi</p>
            ) : (
              <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
                <input type="number" value={mileageVal} onChange={e => setMileageVal(e.target.value)}
                  style={{ width: '140px', padding: '8px 12px', background: '#12121f', border: '1px solid #1e1e35', borderRadius: '8px', color: '#fff', fontSize: '16px', fontWeight: '700', outline: 'none' }} />
                <button onClick={() => { onUpdateMileage(vehicle.id, mileageVal); setEditMileage(false); }}
                  style={{ padding: '8px 16px', background: '#0984e3', color: '#fff', border: 'none', borderRadius: '8px', fontWeight: '600', fontSize: '13px', cursor: 'pointer' }}>Save</button>
              </div>
            )}
          </div>
          {!editMileage && <button onClick={() => setEditMileage(true)} style={{ padding: '8px 14px', background: '#1e1e35', border: 'none', borderRadius: '8px', color: '#aaa', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}>✏️ Update</button>}
        </div>
      </div>

      {/* Reminders */}
      <div style={{ padding: '16px 24px' }}>
        {reminders.map((r, i) => {
          var u = uc[r.urgency] || uc.ok;
          return (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 0', borderBottom: i < reminders.length - 1 ? '1px solid #1e1e3540' : 'none' }}>
              <span style={{ fontSize: '18px' }}>{u.icon}</span>
              <div style={{ flex: 1 }}>
                <p style={{ color: '#fff', fontSize: '14px', fontWeight: '600', margin: '0 0 2px' }}>{r.service}</p>
                <p style={{ color: '#666', fontSize: '11px', margin: 0 }}>{r.description}</p>
              </div>
              <div style={{ textAlign: 'right' }}>
                <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: '6px', fontSize: '10px', fontWeight: '700', color: u.color, background: u.color + '20', marginBottom: '2px' }}>{u.label}</span>
                <p style={{ color: '#555', fontSize: '11px', margin: 0 }}>{r.miles_until_due.toLocaleString()} mi · Every {r.interval_miles.toLocaleString()}</p>
              </div>
            </div>
          );
        })}
        {reminders.length === 0 && (
          <div style={{ textAlign: 'center', padding: '40px', color: '#555' }}>
            <p style={{ fontSize: '32px', marginBottom: '8px' }}>🔔</p>
            <p>Set mileage to see service reminders</p>
          </div>
        )}
      </div>
    </div>
  );
}
